import { Mutex, tryAcquire } from 'async-mutex'
import { Options, Response } from 'request-dot-js'

import { ApiUrl, sendToApi } from 'api/client'

export interface PaginatedApiClientOptions extends Options {
  items?: number | string
  useCursor?: boolean
}

export class PaginatedApiClient<T> {
  public get hasMore(): boolean {
    if (this.options.useCursor) {
      return this.nextCursor !== ''
    }
    return this.nextPage <= this.totalPages
  }

  private _loadMutex: Mutex
  protected callback?: (response: Response<T>) => void
  protected nextPage = 1
  protected options: Options
  protected totalPages = 1
  protected nextCursor?: string = undefined
  protected previousCursor?: string = undefined
  protected url: URL
  public totalCount = 0

  constructor(urlOrPath: string, options: PaginatedApiClientOptions = {}, callback?: (response: Response<T>) => void) {
    this.callback = callback
    this._loadMutex = new Mutex()
    this.options = { isRelative: true, useCursor: false, ...options }
    this.url = new URL(this.options.isRelative ? `${ApiUrl}${urlOrPath}` : urlOrPath)

    // ensure pagination is in use
    this.url.searchParams.set('page', '1')

    if (options.items) {
      this.url.searchParams.set('items', options.items.toString())
    }
  }

  async loadMore(): Promise<Response<T> | null> {
    return tryAcquire(this._loadMutex)
      .runExclusive(async () => {
        if (!this.hasMore) {
          return null
        }

        const response = await sendToApi<T>(this.url.toString(), { ...this.options, isRelative: false })
        if (this.callback) this.callback(response)

        if (response.type !== 'success') {
          return response
        }

        const {
          'current-page': currentPageString,
          'total-count': totalCountString,
          'total-pages': totalPagesString,
          'cursor-next': nextCursor,
          'cursor-previous': previousCursor,
        } = response.headers

        // NOTE: no pagination info present, could be a runtime error
        if (
          (!this.options.useCursor && (!currentPageString || !totalPagesString)) ||
          (this.options.useCursor && !totalCountString)
        ) {
          return response
        }

        const [currentPage, totalCount, totalPages] = [
          Number(currentPageString),
          Number(totalCountString),
          Number(totalPagesString),
        ]
        this.totalCount = totalCount
        this.totalPages = totalPages
        this.nextPage = currentPage + 1
        this.nextCursor = nextCursor
        this.previousCursor = previousCursor

        if (this.options.useCursor) {
          let position = 'after'
          if (this.url.searchParams.has('order')) {
            const order = this.url.searchParams.get('order')
            position = order === 'asc' ? 'after' : 'before'
          }
          this.url.searchParams.set(position, this.nextCursor)
        }

        if (!this.options.useCursor) {
          this.url.searchParams.set('page', this.nextPage.toString())
        }

        return response
      })
      .catch(() => null)
  }
}
